import React, {
  Fragment, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Children as SectionKey } from 'Consts/Questionnaire';
import fr from 'facet-rest';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import LabeledVerticalExpander from '../UI/LabeledVerticalExpander';
import BoolInput from '../Fields/BoolInput';
import DateInput from '../Fields/DateInput';
import FieldList from '../Fields/FieldList';
import RadioInput from '../Fields/RadioInput';
import SelectInput from '../Fields/SelectInput';
import SocialSecurityInput from '../Fields/SocialSecurityInput';
import TextInput from '../Fields/TextInput';
import TextareaInput from '../Fields/TextareaInput';
import Section from '../Section';

function ChildList({
  id, setNames, names, children: removeButton, expanded,
}) {
  const [types, setTypes] = useState([]);
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const { householdResource } = useSimpleHousehold();


  async function getTypes(hhResource) {
    const clientCollection = await hhResource.relationship(fr.relationships.clients.name).getRelated();
    const meta = clientCollection.meta();
    const gradeOptions = meta.field('grade').options().list().map(option => ({
      id: option.id(),
      value: option.attr('prompt'),
    }));
    gradeOptions.unshift({ id: '', value: '- Please select -' });
    setTypes(gradeOptions);
  }

  /* TODO: This is pretty hacky.  This is managing all of the childrens' name state.
    It should be managed somewhere higher in the component tree but the current
    questionnaire architecture doesn't really allow this
  */
  function manageNames() {
    const index = names.findIndex(n => n.id === id);
    const newName = { id, name: childFirstName + childLastName };
    if (index < 0) {
      setNames([...names, newName]);
    } else {
      setNames([...names.slice(0, index), newName, ...names.slice(index + 1)]);
    }
  }

  useEffect(() => {
    if (householdResource) {
      getTypes(householdResource);
    }
  }, [householdResource]);

  useEffect(() => {
    manageNames();
  }, [childFirstName, childLastName]);

  function getChildName() {
    const name = `${childFirstName || ''} ${childLastName || ''}`.trim();
    return name || 'New Child';
  }

  return (
    <LabeledVerticalExpander
      transitionSpeed={0.15}
      title={getChildName()}
      removeButton={removeButton}
      firstElementFocusable
      expanded={expanded}
    >
      <TextInput
        placeholder="First name"
        name={`childFirstName.${id}`}
        answerKey="childFirstName"
        onChange={(name, value) => setChildFirstName(value)}
      />
      <TextInput
        placeholder="Last name"
        name={`childLastName.${id}`}
        answerKey="childLastName"
        onChange={(name, value) => setChildLastName(value)}
      />
      <DateInput
        placeholder="Date of birth"
        name={`childDateOfBirth.${id}`}
        answerKey="childDateOfBirth"
        displayInLocalTime={false}
      />
      <SocialSecurityInput
        placeholder="Social security number"
        name={`childSocialSecurityNumber.${id}`}
        answerKey="childSocialSecurityNumber"
      />
      <RadioInput
        placeholder="From what marriage?"
        name={`childFromWhichMarriage.${id}`}
        answerKey="childFromWhichMarriage"
        options={[
          {
            id: 'current',
            text: 'Current Marriage',
          },
          {
            id: 'previous',
            text: 'Previous Marriage',
          },
          {
            id: 'other',
            text: 'Other',
          },
        ]}
      />
      <BoolInput
        label="Is this child in school?"
        name={`childInSchool.${id}`}
        answerKey="childInSchool"
        checkedChildren={
          <Fragment>
            <RadioInput
              placeholder="What type of school?"
              name={`childSchoolType.${id}`}
              answerKey="childSchoolType"
              options={[
                {
                  id: 'public',
                  text: 'Public',
                },
                {
                  id: 'private',
                  text: 'Private',
                },
              ]}
            />
            <SelectInput
              label="Grade"
              name={`childGrade.${id}`}
              answerKey="childGrade"
              options={types}
            />
          </Fragment>
          }
      />
      <BoolInput
        label="Anticipated education changes?"
        name={`childIsAnticipatedChange.${id}`}
        answerKey="childIsAnticipatedChange"
        checkedChildren={
          <TextareaInput
            placeholder="Anticipated education changes"
            name={`childAnticipatedChange.${id}`}
            answerKey="childAnticipatedChange"
          />
          }
      />
    </LabeledVerticalExpander>
  );
}

ChildList.propTypes = {
  children: PropTypes.node.isRequired,
  expanded: PropTypes.bool,
  id: PropTypes.number.isRequired,
  names: PropTypes.array.isRequired,
  setNames: PropTypes.func.isRequired,
};

ChildList.defaultProps = {
  expanded: false,
};

const Children = () => {
  // Content of names gets edited inside of the Field List of Child fields.
  const [names, setNames] = useState([]);
  const allNamesExist = () => {
    let exists = true;
    names.forEach((n) => {
      if (!n.name) exists = false;
    });
    return exists;
  };

  return (
    <Section
      transitionSpeed={0.25}
      height={400}
      allowContinue={allNamesExist}
      title="Children"
      expanded
      sectionKey={SectionKey}
    >
      <FieldList
        addLabel="Add a child"
        label="Children"
        keyPrefix="child"
        Of={ChildList}
        childProps={{ names, setNames }}
        onRemove={cId => setNames(names.filter(n => n.id !== cId))}
      />
    </Section>
  );
};

Children.propTypes = {};

Children.defaultProps = {};

export default Children;
